import { defineComponent as _defineComponent } from 'vue'
import { openBlock as _openBlock, createElementBlock as _createElementBlock, createCommentVNode as _createCommentVNode, resolveComponent as _resolveComponent, createVNode as _createVNode, unref as _unref, createBlock as _createBlock, renderList as _renderList, Fragment as _Fragment, normalizeClass as _normalizeClass, createElementVNode as _createElementVNode, normalizeStyle as _normalizeStyle, withCtx as _withCtx } from "vue"

const _hoisted_1 = ["id"]
const _hoisted_2 = ["src", "alt"]
const _hoisted_3 = { class: "c-step-by-step-workflow__inner" }
const _hoisted_4 = {
  key: 0,
  class: "c-step-by-step-workflow__header"
}
const _hoisted_5 = { class: "c-step-by-step-workflow__mobile-layout" }
const _hoisted_6 = { class: "c-step-by-step-workflow__grid c-step-by-step-workflow__grid--mobile" }
const _hoisted_7 = {
  key: 0,
  class: "c-step-by-step-workflow__stage"
}
const _hoisted_8 = { class: "c-step-by-step-workflow__cards" }
const _hoisted_9 = ["src", "alt"]
const _hoisted_10 = {
  key: 0,
  class: "c-step-by-step-workflow__mobile-progress",
  "aria-hidden": "true"
}
const _hoisted_11 = { class: "c-step-by-step-workflow__desktop-layout" }
const _hoisted_12 = { class: "c-step-by-step-workflow__desktop-stages" }
const _hoisted_13 = { class: "c-step-by-step-workflow__desktop-cards" }
const _hoisted_14 = ["src", "alt"]

import { computed, nextTick, onBeforeUnmount, onMounted, ref, watch } from 'vue';
import { RichText, Text as JssText } from '@sitecore-jss/sitecore-jss-vue';
import type {
  StepByStepWorkflowCardFields,
  StepByStepWorkflowColumnFields,
  StepByStepWorkflowProps,
} from '@/types/components/StepByStepWorkflow';
import type { ScItem } from '@/types/sitecore';
import utils from '@/utils';

interface NormalizedColumn extends ScItem<StepByStepWorkflowColumnFields> {
  fields: StepByStepWorkflowColumnFields;
}

interface NormalizedCard extends ScItem<StepByStepWorkflowCardFields> {
  fields: StepByStepWorkflowCardFields;
}

interface DesktopCardItem {
  column: NormalizedColumn;
  columnIndex: number;
  cardIndex: number;
  item: NormalizedCard;
}

interface DesktopStageItem {
  column: NormalizedColumn;
  columnIndex: number;
  span: number;
  hasContent: boolean;
}

type WorkflowCardTone = (typeof validTones)[number];


export default /*@__PURE__*/_defineComponent({
  __name: 'StepByStepWorkflow',
  props: {
    dataSource: {},
    fields: {},
    rendering: {},
    params: {}
  },
  setup(__props: any) {

const props = __props;
const fields = computed(() => props.fields);
const rendering = computed(() => props.rendering);
const mobileScrollEl = ref<HTMLElement | null>(null);
const mobileProgress = ref(0);
const hasMobileOverflow = ref(false);
let mobileResizeObserver: ResizeObserver | null = null;

const rootClass = computed(() =>
  utils.overall.baseComClass(props.fields, 'c-step-by-step-workflow')
);
const rootStyle = computed(() => utils.overall?.baseComStyle?.(fields.value) || {});
const backgroundSrc = computed(() => fields.value?.backgroundImage?.value?.src || '');
const backgroundAlt = computed(() => fields.value?.backgroundImage?.value?.alt || '');
const hasHeader = computed(
  () => Boolean(fields.value?.title?.value) || Boolean(fields.value?.description?.value)
);

const rawColumns = computed<ScItem<StepByStepWorkflowColumnFields>[]>(() => {
  const columnItems = utils.sc.getFieldObj(fields.value, 'columns');
  return Array.isArray(columnItems) ? columnItems : [];
});

const columns = computed<NormalizedColumn[]>(() =>
  rawColumns.value.filter((column): column is NormalizedColumn => Boolean(column.fields))
);
const columnsCount = computed(() => `${Math.max(columns.value.length, 1)}`);

const getCards = (column: NormalizedColumn): NormalizedCard[] => {
  const cards = utils.sc.getFieldObj(column.fields, 'cards');
  return Array.isArray(cards)
    ? cards.filter((card): card is NormalizedCard => Boolean(card.fields?.body?.value))
    : [];
};

const hasStageContent = (column: NormalizedColumn) =>
  Boolean(column.fields.title?.value) || Boolean(column.fields.eyebrow?.value);

const desktopCards = computed<DesktopCardItem[]>(() =>
  columns.value.flatMap((column, columnIndex) =>
    getCards(column).map((item, cardIndex) => ({
      column,
      columnIndex,
      cardIndex,
      item,
    }))
  )
);

const parseHeaderSpan = (column: NormalizedColumn, columnIndex: number) => {
  const rawSpan = Number(column.fields.headerSpan?.value || 1);
  const span = Number.isFinite(rawSpan) ? Math.floor(rawSpan) : 1;
  return Math.min(Math.max(span, 1), columns.value.length - columnIndex);
};

const desktopStages = computed<DesktopStageItem[]>(() => {
  const stages: DesktopStageItem[] = [];

  for (let columnIndex = 0; columnIndex < columns.value.length;) {
    const column = columns.value[columnIndex];
    const span = parseHeaderSpan(column, columnIndex);
    stages.push({ column, columnIndex, span, hasContent: hasStageContent(column) });
    columnIndex += span;
  }

  return stages;
});

const validTones = ['orange', 'green', 'white'] as const;
const isWorkflowCardTone = (tone: string): tone is WorkflowCardTone =>
  validTones.includes(tone as WorkflowCardTone);

const getDefaultTone = (cardIndex: number): WorkflowCardTone => {
  if (cardIndex === 0) return 'orange';
  if (cardIndex === 1) return 'green';
  return 'white';
};

const getTone = (card: NormalizedCard, cardIndex: number): WorkflowCardTone => {
  const toneField = card.fields.tone;
  const tone =
    ('fields' in (toneField || {}) ? toneField?.fields?.phrase?.value : toneField?.value)
      ?.trim()
      .toLowerCase() || '';
  return isWorkflowCardTone(tone) ? tone : getDefaultTone(cardIndex);
};

const getDesktopCardStyle = (card: DesktopCardItem) => {
  return {
    '--workflow-column-start': `${card.columnIndex + 1}`,
    '--workflow-row-start': `${card.cardIndex + 1}`,
  };
};

const getDesktopStageStyle = (stage: DesktopStageItem) => {
  return {
    '--workflow-column-start': `${stage.columnIndex + 1}`,
    '--workflow-column-span': `${stage.span}`,
  };
};

const getIconSrc = (card: NormalizedCard) => card.fields.icon?.value?.src || '';
const getIconAlt = (card: NormalizedCard) =>
  card.fields.icon?.value?.alt || card.fields.icon?.value?.title || '';

const mobileProgressStyle = computed(() => ({
  transform: `scaleX(${Math.max(mobileProgress.value, 0.28)})`,
}));

const updateMobileScroll = () => {
  const el = mobileScrollEl.value;
  if (!el) return;

  const maxScroll = el.scrollWidth - el.clientWidth;
  hasMobileOverflow.value = maxScroll > 1;
  mobileProgress.value = maxScroll > 0 ? el.scrollLeft / maxScroll : 1;
};

onMounted(() => {
  nextTick(updateMobileScroll);

  if (typeof ResizeObserver !== 'undefined' && mobileScrollEl.value) {
    mobileResizeObserver = new ResizeObserver(updateMobileScroll);
    mobileResizeObserver.observe(mobileScrollEl.value);
  }
});

onBeforeUnmount(() => {
  mobileResizeObserver?.disconnect();
});

watch(columns, () => nextTick(updateMobileScroll));

return (_ctx: any,_cache: any) => {
  const _component_JssRichText = _resolveComponent("JssRichText")!
  const _component_ContentGrid = _resolveComponent("ContentGrid")!

  return (fields.value)
    ? (_openBlock(), _createElementBlock("section", {
        key: 0,
        class: _normalizeClass(rootClass.value),
        style: _normalizeStyle(rootStyle.value),
        id: rendering.value?.dataSource
      }, [
        (backgroundSrc.value)
          ? (_openBlock(), _createElementBlock("img", {
              key: 0,
              class: "c-step-by-step-workflow__bg",
              src: backgroundSrc.value,
              alt: backgroundAlt.value,
              loading: "lazy"
            }, null, 8, _hoisted_2))
          : _createCommentVNode("", true),
        _createVNode(_component_ContentGrid, {
          "desktop-default-grid": [2, 20],
          "mobile-default-grid": [1, 14]
        }, {
          default: _withCtx(() => [
            _createElementVNode("div", _hoisted_3, [
              (hasHeader.value)
                ? (_openBlock(), _createElementBlock("header", _hoisted_4, [
                    _createVNode(_component_JssRichText, {
                      field: fields.value?.title,
                      tag: "h2"
                    }, null, 8, ["field"]),
                    (fields.value.description?.value)
                      ? (_openBlock(), _createBlock(_unref(RichText), {
                          key: 0,
                          class: "c-step-by-step-workflow__description",
                          field: fields.value.description
                        }, null, 8, ["field"]))
                      : _createCommentVNode("", true)
                  ]))
                : _createCommentVNode("", true),
              (columns.value.length)
                ? (_openBlock(), _createElementBlock("div", {
                    key: 1,
                    class: _normalizeClass(["c-step-by-step-workflow__panel", { 'c-step-by-step-workflow__panel--with-header': hasHeader.value }]),
                    style: _normalizeStyle({ '--workflow-columns': columnsCount.value })
                  }, [
                    _createElementVNode("div", _hoisted_5, [
                      _createElementVNode("div", {
                        ref_key: "mobileScrollEl",
                        ref: mobileScrollEl,
                        class: "c-step-by-step-workflow__mobile-scroll",
                        onScroll: updateMobileScroll
                      }, [
                        _createElementVNode("div", _hoisted_6, [
                          (_openBlock(true), _createElementBlock(_Fragment, null, _renderList(columns.value, (column) => {
                            return (_openBlock(), _createElementBlock("article", {
                              key: column.id,
                              class: "c-step-by-step-workflow__column"
                            }, [
                              (hasStageContent(column))
                                ? (_openBlock(), _createElementBlock("header", _hoisted_7, [
                                    (column.fields.eyebrow?.value)
                                      ? (_openBlock(), _createBlock(_unref(JssText), {
                                          key: 0,
                                          class: "c-step-by-step-workflow__eyebrow",
                                          field: column.fields.eyebrow,
                                          tag: "p"
                                        }, null, 8, ["field"]))
                                      : _createCommentVNode("", true),
                                    (column.fields.title?.value)
                                      ? (_openBlock(), _createBlock(_unref(JssText), {
                                          key: 1,
                                          class: "c-step-by-step-workflow__stage-title",
                                          field: column.fields.title,
                                          tag: "h3"
                                        }, null, 8, ["field"]))
                                      : _createCommentVNode("", true)
                                  ]))
                                : _createCommentVNode("", true),
                              _createElementVNode("div", _hoisted_8, [
                                (_openBlock(true), _createElementBlock(_Fragment, null, _renderList(getCards(column), (card, cardIndex) => {
                                  return (_openBlock(), _createElementBlock("div", {
                                    key: card.id,
                                    class: _normalizeClass(["c-step-by-step-workflow__card", [
                        `c-step-by-step-workflow__card--${getTone(card, cardIndex)}`,
                        {
                          'c-step-by-step-workflow__card--emphasis': card.fields.emphasis?.value,
                        },
                      ]])
                                  }, [
                                    (getIconSrc(card))
                                      ? (_openBlock(), _createElementBlock("img", {
                                          key: 0,
                                          class: "c-step-by-step-workflow__card-icon",
                                          src: getIconSrc(card),
                                          alt: getIconAlt(card),
                                          loading: "lazy"
                                        }, null, 8, _hoisted_9))
                                      : _createCommentVNode("", true),
                                    _createVNode(_unref(RichText), {
                                      class: "c-step-by-step-workflow__card-body",
                                      field: card.fields.body
                                    }, null, 8, ["field"])
                                  ], 2))
                                }), 128))
                              ])
                            ]))
                          }), 128))
                        ])
                      ], 544),
                      (hasMobileOverflow.value)
                        ? (_openBlock(), _createElementBlock("div", _hoisted_10, [
                            _createElementVNode("span", {
                              style: _normalizeStyle(mobileProgressStyle.value)
                            }, null, 4)
                          ]))
                        : _createCommentVNode("", true)
                    ]),
                    _createElementVNode("div", _hoisted_11, [
                      _createElementVNode("div", _hoisted_12, [
                        (_openBlock(true), _createElementBlock(_Fragment, null, _renderList(desktopStages.value, (stage) => {
                          return (_openBlock(), _createElementBlock("header", {
                            key: `${stage.column.id}-desktop-stage`,
                            class: _normalizeClass(["c-step-by-step-workflow__stage c-step-by-step-workflow__stage--desktop", {
                  'c-step-by-step-workflow__stage--desktop-empty': !stage.hasContent,
                  'c-step-by-step-workflow__stage--desktop-centered': stage.span > 1,
                }]),
                            style: _normalizeStyle(getDesktopStageStyle(stage))
                          }, [
                            (stage.column.fields.eyebrow?.value)
                              ? (_openBlock(), _createBlock(_unref(JssText), {
                                  key: 0,
                                  class: "c-step-by-step-workflow__eyebrow",
                                  field: stage.column.fields.eyebrow,
                                  tag: "p"
                                }, null, 8, ["field"]))
                              : _createCommentVNode("", true),
                            (stage.column.fields.title?.value)
                              ? (_openBlock(), _createBlock(_unref(JssText), {
                                  key: 1,
                                  class: "c-step-by-step-workflow__stage-title",
                                  field: stage.column.fields.title,
                                  tag: "h3"
                                }, null, 8, ["field"]))
                              : _createCommentVNode("", true)
                          ], 6))
                        }), 128))
                      ]),
                      _createElementVNode("div", _hoisted_13, [
                        (_openBlock(true), _createElementBlock(_Fragment, null, _renderList(desktopCards.value, (card) => {
                          return (_openBlock(), _createElementBlock("div", {
                            key: `${card.column.id}-${card.item.id}`,
                            class: _normalizeClass(["c-step-by-step-workflow__card c-step-by-step-workflow__card--desktop", [
                  `c-step-by-step-workflow__card--${getTone(card.item, card.cardIndex)}`,
                  {
                    'c-step-by-step-workflow__card--emphasis': card.item.fields.emphasis?.value,
                  },
                ]]),
                            style: _normalizeStyle(getDesktopCardStyle(card))
                          }, [
                            (getIconSrc(card.item))
                              ? (_openBlock(), _createElementBlock("img", {
                                  key: 0,
                                  class: "c-step-by-step-workflow__card-icon",
                                  src: getIconSrc(card.item),
                                  alt: getIconAlt(card.item),
                                  loading: "lazy"
                                }, null, 8, _hoisted_14))
                              : _createCommentVNode("", true),
                            _createVNode(_unref(RichText), {
                              class: "c-step-by-step-workflow__card-body",
                              field: card.item.fields.body
                            }, null, 8, ["field"])
                          ], 6))
                        }), 128))
                      ])
                    ])
                  ], 6))
                : _createCommentVNode("", true)
            ])
          ]),
          _: 1
        })
      ], 14, _hoisted_1))
    : _createCommentVNode("", true)
}
}

})